import * as React from 'react';
import { Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { dayjs } from '../ex_dayjs';
import MDateControl from './MDateControl';

/** Any value accepted as a date input by this control. */
type MDateRangeValue = Dayjs | Date | string | number | null | undefined;

/**
 * Props for {@link MDateRangePicker}.
 */
interface MDateRangePickerProps extends Omit<React.ComponentProps<typeof MDateControl>, 'defaultValue' | 'onChange' | 'label' | 'isEndOfDate'> {
    /** `dayjs` format string. Defaults to `'MM/DD/YYYY'`. */
    format?: string;
    /** Whether the reported values are converted to UTC. Defaults to `false`. */
    isUTC?: boolean;
    /** Field label applied to the "from" picker. */
    label?: string;
    /** Base OData field name; combined with `_ge`/`_le` suffixes when reporting changes. */
    name?: string;
    /**
     * Called once per bound whenever either the "from" or "to" date changes.
     *
     * @param formatted - The formatted date string, or `null` if cleared/invalid.
     * @param filterName - The OData field name with its `_ge`/`_le` suffix.
     */
    onChange: (formatted: string | null, filterName: string) => void;
    /** Initial `[from, to]` values. */
    defaultValue?: [MDateRangeValue, MDateRangeValue];
}

/**
 * A pair of {@link MDateControl} pickers ("from" / "to") sharing a `dayjs`
 * {@link LocalizationProvider}, reporting each bound independently via
 * `onChange` suffixed with the `_ge`/`_le` OData operators. The "to" picker
 * is shifted to the end of the selected day.
 */
const MDateRangePicker: React.FC<MDateRangePickerProps> = ({
    format = 'MM/DD/YYYY',
    isUTC = false,
    label, name, onChange, defaultValue, ...props
}) => {
    const [from, setFrom] = React.useState<Dayjs | undefined>();
    const [to, setTo] = React.useState<Dayjs | undefined>();

    React.useEffect(() => {
        if (defaultValue?.[0]) setFrom(dayjs(defaultValue[0]));
        if (defaultValue?.[1]) setTo(dayjs(defaultValue[1]));
    }, [defaultValue]);

    const onChangeDateFrom = (newValue: string | null) => {
        onChange(newValue, `${name}_ge`);
    };

    const onChangeDateTo = (newValue: string | null) => {
        onChange(newValue, `${name}_le`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container>
                <Grid size={6}>
                    <MDateControl
                        {...props}
                        label={label}
                        defaultValue={from}
                        format={format}
                        isUTC={isUTC}
                        onChange={onChangeDateFrom}
                    />
                </Grid>
                <Grid size={6}>
                    <MDateControl
                        {...props}
                        label={'to'}
                        defaultValue={to}
                        isUTC={isUTC}
                        isEndOfDate={true}
                        format={format}
                        onChange={onChangeDateTo} />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};

export default React.memo(MDateRangePicker);
